import { baseApi } from '@api/baseApi';
import { ReducedOrdersReport } from '@api/order-reporting/types/ReducedOrdersReport';
import { ReducedMaterialsReport } from '@api/order-reporting/types/ReducedMaterialsReport';
import { RedatedOrdersReport } from '@api/order-reporting/types/RedatedOrdersReport';
import { ReducedParams } from '@api/order-reporting/types/ReducedParams';
import { RedatedParams } from '@api/order-reporting/types/RedatedParams';
import { ShipmentStopsReport } from '@api/order-reporting/types/ShipmentStopsReport';
import { ShipmentStopsParams, ShipmentStopsTilesParams } from '@api/order-reporting/types/ShipmentStopsParams';
import { UndeliveredParams } from '@api/order-reporting/types/UndeliveredParams';
import { UndeliveredOrdersReport } from '@api/order-reporting/types/UndeliveredOrdersReport';
import { ShipmentStopsTilesContainer } from '@api/order-reporting/types/ShipmentStopsTilesContainer';



export const orderReportingApi = baseApi.injectEndpoints({
	overrideExisting: false,
	endpoints: (builder) => ({

		reducedOrders: builder.query<ReducedOrdersReport[], ReducedParams>({
			query: ({ dateFrom, dateTo, customerId, dc, materialId, region, district, orderPhase }) => ({
				url: `OrderReporting/GetReducedOrdersReport?dateFrom=${dateFrom}&dateTo=${dateTo}`.concat(
					customerId ? customerId.split(',').map(x => `&customerId=${x}`).join('') : '',
					dc ? dc.split(',').map(x => `&dc=${x}`).join('') : '',
					materialId ? materialId.split(',').map(x => `&materialId=${x}`).join('') : '',
					region ? region.split(',').map(x => `&region=${x}`).join('') : '',
					district ? district.split(',').map(x => `&district=${x}`).join('') : '',
					orderPhase ? `&orderPhase=${orderPhase}` : ''),
			}),
			providesTags: ['reducedOrdersReport']
		}),

		resetReducedOrders: builder.mutation<null, void>({
			queryFn: () => ({ data: null }),
			invalidatesTags: ['reducedOrdersReport'],
		}),


		reducedMaterials: builder.query<ReducedMaterialsReport[], ReducedParams>({
			query: ({ dateFrom, dateTo, customerId, dc, materialId, region, district, orderPhase }) => ({
				url: `OrderReporting/GetReducedOrdersMaterialsReport?dateFrom=${dateFrom}&dateTo=${dateTo}`.concat(
					customerId ? customerId.split(',').map(x => `&customerId=${x}`).join('') : '',
					dc ? dc.split(',').map(x => `&dc=${x}`).join('') : '',
					materialId ? materialId.split(',').map(x => `&materialId=${x}`).join('') : '',
					region ? region.split(',').map(x => `&region=${x}`).join('') : '',
					district ? district.split(',').map(x => `&district=${x}`).join('') : '',
					orderPhase ? `&orderPhase=${orderPhase}` : ''),
			}),
			providesTags: ['reducedMaterialsReport']
		}),

		resetReducedMaterials: builder.mutation<null, void>({
			queryFn: () => ({ data: null }),
			invalidatesTags: ['reducedMaterialsReport'],
		}),


		redatedOrders: builder.query<RedatedOrdersReport[], RedatedParams>({
			query: ({ dateFrom, dateTo, customerId, dc, materialId, region, district, filterDate }) => ({
				url: `OrderReporting/GetRedatedOrdersReport?dateFrom=${dateFrom}&dateTo=${dateTo}`.concat(
					customerId ? customerId.split(',').map(x => `&customerId=${x}`).join('') : '',
					dc ? dc.split(',').map(x => `&dc=${x}`).join('') : '',
					materialId ? materialId.split(',').map(x => `&materialId=${x}`).join('') : '',
					region ? region.split(',').map(x => `&region=${x}`).join('') : '',
					district ? district.split(',').map(x => `&district=${x}`).join('') : '',
					filterDate ? `&filterDate=${filterDate}` : ''),
			}),
			providesTags: ['redatedOrdersReport']
		}),

		resetRedatedOrders: builder.mutation<null, void>({
			queryFn: () => ({ data: null }),
			invalidatesTags: ['redatedOrdersReport'],
		}),


		undeliveredOrders: builder.query<UndeliveredOrdersReport[], UndeliveredParams>({
			query: ({ dateFrom, dateTo, customerId, dc, materialId, region, district, phase }) => ({
				url: `OrderReporting/GetUndeliveredOrdersReport?dateFrom=${dateFrom}&dateTo=${dateTo}`.concat(
					customerId ? customerId.split(',').map(x => `&customerId=${x}`).join('') : '',
					dc ? dc.split(',').map(x => `&dc=${x}`).join('') : '',
					materialId ? materialId.split(',').map(x => `&materialId=${x}`).join('') : '',
					region ? region.split(',').map(x => `&region=${x}`).join('') : '',
					district ? district.split(',').map(x => `&district=${x}`).join('') : '',
					phase ? `&phase=${phase}` : ''),
			}),
			providesTags: ['undeliveredOrdersReport']
		}),

		resetUndeliveredOrders: builder.mutation<null, void>({
			queryFn: () => ({ data: null }),
			invalidatesTags: ['undeliveredOrdersReport'],
		}),


		shipmentStops: builder.query<ShipmentStopsReport[], ShipmentStopsParams>({
			query: (shipmentStopsParams) => ({
				url: 'OrderReporting/GetShipmentStopsReport',
				params: shipmentStopsParams
			}),
			providesTags: ['shipmentStopsReport']
		}),

		resetShipmentStops: builder.mutation<null, void>({
			queryFn: () => ({ data: null }),
			invalidatesTags: ['shipmentStopsReport'],
		}),

		shipmentStopsTiles: builder.query<ShipmentStopsTilesContainer, ShipmentStopsTilesParams>({
			query: (shipmentStopsTilesParams) => ({
				url: 'OrderReporting/GetShipmentStopsReportTiles',
				params: shipmentStopsTilesParams
			}),
			providesTags: ['shipmentStopsTiles']
		}),

		resetShipmentStopsTiles: builder.mutation<null, void>({
			queryFn: () => ({ data: null }),
			invalidatesTags: ['shipmentStopsTiles'],
		}),
	}),
});


export const { 
	useLazyReducedOrdersQuery,
	useResetReducedOrdersMutation,
	useLazyReducedMaterialsQuery,
	useResetReducedMaterialsMutation,
	useLazyRedatedOrdersQuery,
	useResetRedatedOrdersMutation,
	useLazyUndeliveredOrdersQuery,
	useResetUndeliveredOrdersMutation,
	useLazyShipmentStopsQuery,
	useResetShipmentStopsMutation,
	useLazyShipmentStopsTilesQuery,
	useResetShipmentStopsTilesMutation } = orderReportingApi;